import React, { useEffect, useRef } from 'react';
import { TrendingUpIcon } from 'lucide-react';
export const KidsBenefitsSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Animation for benefit items
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, {
      threshold: 0.1
    });
    const benefitItems = document.querySelectorAll('.benefit-item');
    benefitItems.forEach(item => {
      observer.observe(item);
    });
    // Animated circles
    const circles = document.querySelectorAll('.animated-circle');
    // Random movement function
    const animateCircles = () => {
      circles.forEach(circle => {
        const element = circle as HTMLElement;
        // Random values for animation
        const duration = 3 + Math.random() * 7; // 3-10 seconds
        const xMove = -20 + Math.random() * 40; // -20px to +20px
        const yMove = -20 + Math.random() * 40; // -20px to +20px
        const delay = Math.random() * 5; // 0-5 seconds
        // Apply animation
        element.style.animation = `float ${duration}s ease-in-out infinite`;
        element.style.transform = `translate(${xMove}px, ${yMove}px)`;
        element.style.animationDelay = `${delay}s`;
      });
    };
    animateCircles();
    return () => {
      benefitItems.forEach(item => {
        observer.unobserve(item);
      });
    };
  }, []);
  return <section ref={sectionRef} className="bg-gradient-to-r from-primary-50 to-secondary-50 py-16 md:py-24 overflow-hidden" data-id="element-335">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-336">
        {/* Animated background elements */}
        <div className="absolute top-20 right-20 w-24 h-24 bg-secondary-200 rounded-full opacity-20 animated-circle" data-id="element-337"></div>
        <div className="absolute bottom-20 left-20 w-16 h-16 bg-primary-200 rounded-full opacity-20 animated-circle" data-id="element-338"></div>
        <div className="absolute top-40 left-40 w-12 h-12 bg-primary-300 rounded-full opacity-10 animated-circle" data-id="element-339"></div>
        <div className="absolute bottom-40 right-40 w-18 h-18 bg-secondary-300 rounded-full opacity-20 animated-circle" data-id="element-340"></div>
        <div className="absolute top-10 left-1/4 w-8 h-8 bg-primary-400 rounded-full opacity-15 animated-circle" data-id="element-341"></div>
        <div className="absolute bottom-10 right-1/4 w-10 h-10 bg-secondary-400 rounded-full opacity-15 animated-circle" data-id="element-342"></div>
        <div className="flex flex-col lg:flex-row items-center gap-12" data-id="element-343">
          <div className="lg:w-1/2" data-id="element-344">
            <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-345">
              Why Kids <span className="text-primary-300" data-id="element-346">Love</span> Our Soft
              Play Experiences
            </h2>
            <div className="space-y-12" data-id="element-347">
              <div className="benefit-item flex gap-6 opacity-0 transition-opacity duration-700" data-id="element-348">
                <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-500 rounded-2xl text-3xl font-bold" data-id="element-349">
                  1
                </div>
                <div data-id="element-350">
                  <h3 className="text-2xl font-bold mb-2" data-id="element-351">Safe Exploration</h3>
                  <p className="text-gray-600" data-id="element-352">
                    Our equipment is designed with safety in mind, allowing
                    children to explore and play confidently.
                  </p>
                </div>
              </div>
              <div className="benefit-item flex gap-6 opacity-0 transition-opacity duration-700 delay-300" data-id="element-353">
                <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-secondary-100 text-secondary-500 rounded-2xl text-3xl font-bold" data-id="element-354">
                  2
                </div>
                <div data-id="element-355">
                  <h3 className="text-2xl font-bold mb-2" data-id="element-356">
                    Sensory Development
                  </h3>
                  <p className="text-gray-600" data-id="element-357">
                    Diverse textures, colors, and play elements stimulate
                    sensory development and motor skills.
                  </p>
                </div>
              </div>
              <div className="benefit-item flex gap-6 opacity-0 transition-opacity duration-700 delay-500" data-id="element-358">
                <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-500 rounded-2xl text-3xl font-bold" data-id="element-359">
                  3
                </div>
                <div data-id="element-360">
                  <h3 className="text-2xl font-bold mb-2" data-id="element-361">
                    Imagination & Creativity
                  </h3>
                  <p className="text-gray-600" data-id="element-362">
                    Open-ended play setups encourage children to use their
                    imagination and create their own adventures.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:w-1/2 relative" data-id="element-363">
            <div className="relative" data-id="element-364">
              <div className="absolute -top-6 -right-6 bg-white rounded-full p-6 shadow-lg z-10 animated-circle" data-id="element-365">
                <TrendingUpIcon className="h-10 w-10 text-primary-500" data-id="element-366" />
              </div>
              {/* Main circles with floating dots */}
              <div className="w-full h-full flex justify-center" data-id="element-367">
                <div className="w-64 h-64 bg-secondary-300 rounded-full flex items-center justify-center animated-circle relative" data-id="element-368">
                  {/* Decorative floating dots inside the outer circle */}
                  <div className="absolute top-1/4 left-1/4 w-6 h-6 bg-primary-200 rounded-full opacity-60 animated-circle" data-id="element-369"></div>
                  <div className="absolute bottom-1/4 right-1/4 w-8 h-8 bg-secondary-200 rounded-full opacity-60 animated-circle" data-id="element-370"></div>
                  <div className="w-48 h-48 bg-secondary-400 rounded-full flex items-center justify-center animated-circle relative" data-id="element-371">
                    {/* More decorative floating dots */}
                    <div className="absolute top-1/3 right-1/5 w-4 h-4 bg-primary-300 rounded-full opacity-70 animated-circle" data-id="element-372"></div>
                    <div className="absolute bottom-1/3 left-1/5 w-5 h-5 bg-secondary-300 rounded-full opacity-70 animated-circle" data-id="element-373"></div>
                    <div className="relative w-40 h-40 bg-secondary-500 rounded-full flex items-center justify-center animated-circle" data-id="element-374">
                      {/* Smiley face in the innermost circle */}
                      <div className="relative w-full h-full" data-id="element-375">
                        {/* Eyes */}
                        <div className="absolute top-1/3 left-1/3 w-4 h-4 bg-white rounded-full" data-id="element-376"></div>
                        <div className="absolute top-1/3 right-1/3 w-4 h-4 bg-white rounded-full" data-id="element-377"></div>
                        {/* Smile */}
                        <div className="absolute bottom-1/3 left-1/2 transform -translate-x-1/2 w-16 h-8 border-b-4 border-white rounded-b-full" data-id="element-378"></div>
                      </div>
                      {/* Moved decorative elements away from the face */}
                      <div className="absolute -top-6 -left-6 w-8 h-8 bg-primary-400 rounded-md rotate-45 animated-circle" data-id="element-379"></div>
                      <div className="absolute -bottom-6 -right-6 w-8 h-8 bg-primary-400 rounded-md rotate-45 animated-circle" data-id="element-380"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Small decorative circle in the corner */}
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg animated-circle" data-id="element-381">
              <div className="w-12 h-12 bg-secondary-400 rounded-full flex items-center justify-center relative" data-id="element-382">
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animated-circle" data-id="element-383"></div>
                <div className="absolute bottom-1/4 right-1/4 w-3 h-3 bg-white rounded-full animated-circle" data-id="element-384"></div>
                <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-primary-300 rounded-full animated-circle" data-id="element-385"></div>
              </div>
            </div>
            {/* Additional decorative floating elements */}
            <div className="absolute top-1/4 left-1/4 w-10 h-10 bg-primary-200 rounded-full opacity-30 animated-circle" data-id="element-386"></div>
            <div className="absolute bottom-1/4 right-1/3 w-8 h-8 bg-secondary-200 rounded-full opacity-30 animated-circle" data-id="element-387"></div>
            <div className="absolute top-1/2 right-1/4 w-6 h-6 bg-primary-300 rounded-full opacity-20 animated-circle" data-id="element-388"></div>
            <div className="absolute bottom-1/3 left-1/3 w-12 h-12 bg-secondary-100 rounded-full opacity-20 animated-circle" data-id="element-389"></div>
          </div>
        </div>
      </div>
    </section>;
};